/**
 * REST-backed Arena services (user / saveGame / leaderboard / inventory),
 * surfaced to the rest of the codebase via the `Arena` singleton in arena.js.
 */

const STORAGE_KEY = 'arena.authToken';

function getToken() { try { return localStorage.getItem(STORAGE_KEY); } catch { return null; } }
function setToken(t) { try { localStorage.setItem(STORAGE_KEY, t); } catch {} }

let _authPromise = null;
let _authCache = null;

export function buildRestShims({ apiUrl }) {
    async function api(path, opts = {}) {
        const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
        const tok = getToken();
        if (tok) headers.Authorization = `Bearer ${tok}`;
        const res = await fetch(`${apiUrl}${path}`, { ...opts, headers });
        if (!res.ok) {
            // Surface server-provided error messages (e.g. "Username already taken")
            // instead of a bare status code, so account/shop UIs can show them verbatim.
            let serverMsg = '';
            try {
                const body = await res.json();
                serverMsg = body?.error || body?.message || '';
            } catch { /* non-JSON body — ignore */ }
            const err = new Error(serverMsg || `Request failed (${res.status})`);
            err.status = res.status;
            throw err;
        }
        return res.status === 204 ? null : res.json();
    }

    /** Returns { token, accountId, name } — guest by default; cached for the session. */
    async function ensureAuth() {
        if (_authCache) return _authCache;
        if (_authPromise) return _authPromise;
        _authPromise = (async () => {
            const tok = getToken();
            if (tok) {
                try {
                    const me = await api('/auth/me');
                    _authCache = {
                        token: tok,
                        accountId: me.accountId,
                        name: me.name,
                        email: me.email || null,
                        emailVerified: !!me.emailVerified,
                        hasPassword: !!me.hasPassword,
                    };
                    return _authCache;
                } catch { /* token expired → fall through */ }
            }
            const res = await api('/auth/guest');
            setToken(res.token);
            _authCache = {
                token: res.token, accountId: res.accountId, name: res.name,
                email: null, emailVerified: false, hasPassword: false,
            };
            return _authCache;
        })();
        return _authPromise;
    }

    const user = {
        get id() { return _authCache?.accountId || null; },
        getName: async () => (await ensureAuth()).name,
        /** True if the current account is recoverable via username + password. */
        hasPassword: async () => !!(await ensureAuth()).hasPassword,
        /** Current email + verification status (null if none on file). */
        getEmail: async () => {
            const me = await ensureAuth();
            return { email: me.email || null, verified: !!me.emailVerified };
        },
        /** Claim a custom display name on the current (possibly guest) account. */
        setName: async (name) => {
            await ensureAuth();
            const res = await api('/auth/name', {
                method: 'PATCH',
                body: JSON.stringify({ name }),
            });
            setToken(res.token);
            _authCache = { ..._authCache, token: res.token, accountId: res.accountId, name: res.name };
            return _authCache;
        },
        /** Attach a password to a passwordless account so it survives token loss. */
        setPassword: async (password) => {
            await ensureAuth();
            await api('/auth/set-password', {
                method: 'POST',
                body: JSON.stringify({ password }),
            });
            if (_authCache) _authCache.hasPassword = true;
            return true;
        },
        /** Change an existing password (requires knowledge of the current one). */
        changePassword: async (currentPassword, newPassword) => {
            await ensureAuth();
            await api('/auth/change-password', {
                method: 'POST',
                body: JSON.stringify({ currentPassword, newPassword }),
            });
            return true;
        },
        /** Attach or change the email address on the current account. Triggers a
         *  verification mail; the address is treated as unverified until clicked. */
        setEmail: async (email) => {
            await ensureAuth();
            const res = await api('/auth/set-email', {
                method: 'POST',
                body: JSON.stringify({ email }),
            });
            if (_authCache) { _authCache.email = email; _authCache.emailVerified = false; }
            return res;
        },
        /** Resend the verification mail for the currently-attached email. */
        resendVerification: async () => {
            await ensureAuth();
            return api('/auth/resend-verification', { method: 'POST' });
        },
        /** Consume a verification token from a mailed link (no auth required). */
        verifyEmail: async (token) => api('/auth/verify-email', {
            method: 'POST',
            body: JSON.stringify({ token }),
        }),
        /** Trigger sending a password-reset mail. Always resolves OK to avoid
         *  leaking which addresses are registered (server enforces same). */
        requestPasswordReset: async (email) => api('/auth/request-password-reset', {
            method: 'POST',
            body: JSON.stringify({ email }),
        }),
        /** Complete a password-reset using the token from the mailed link.
         *  On success the session is switched to the reset account. */
        resetPassword: async (token, newPassword) => {
            const res = await api('/auth/reset-password', {
                method: 'POST',
                body: JSON.stringify({ token, newPassword }),
            });
            setToken(res.token);
            _authCache = {
                token: res.token, accountId: res.accountId, name: res.name,
                email: res.email || null, emailVerified: !!res.emailVerified,
                hasPassword: true,
            };
            return _authCache;
        },
        /** Create a password-protected account and switch the session to it. */
        register: async (name, password, opts = {}) => {
            const body = { name, password, acceptTerms: true };
            if (opts.email) body.email = opts.email;
            const res = await api('/auth/register', {
                method: 'POST',
                body: JSON.stringify(body),
            });
            setToken(res.token);
            _authCache = {
                token: res.token, accountId: res.accountId, name: res.name,
                email: res.email || null, emailVerified: !!res.emailVerified,
                hasPassword: true,
            };
            return _authCache;
        },
        /** Log in to an existing account using either display name or email. */
        login: async (identifier, password) => {
            const res = await api('/auth/login', {
                method: 'POST',
                body: JSON.stringify({ identifier, password }),
            });
            setToken(res.token);
            _authCache = {
                token: res.token, accountId: res.accountId, name: res.name,
                email: res.email || null, emailVerified: !!res.emailVerified,
                hasPassword: true,
            };
            return _authCache;
        },
        /** Download a JSON export of all account data (Art. 15 DSGVO). */
        exportData: async () => {
            await ensureAuth();
            const tok = getToken();
            const res = await fetch(`${apiUrl}/account/export`, {
                headers: { Authorization: `Bearer ${tok}` },
            });
            if (!res.ok) throw new Error(`Export failed (${res.status})`);
            return res.json();
        },
        /** Permanently delete the account (Art. 17 DSGVO). Logs the session out. */
        deleteAccount: async () => {
            await ensureAuth();
            await api('/account', { method: 'DELETE' });
            try { localStorage.removeItem(STORAGE_KEY); } catch {}
            _authCache = null; _authPromise = null;
            return true;
        },
        /** Log out without deleting anything. */
        logout: () => {
            try { localStorage.removeItem(STORAGE_KEY); } catch {}
            _authCache = null; _authPromise = null;
        },
    };

    // Legacy heyVR shape: slots use `save_data` (snake_case) and leaderboard
    // rows expose `user` (display name). The REST server returns camelCase
    // `saveData` / `name` so we remap here to keep call sites untouched.
    const remapSlot = (s) => (s ? { ...s, save_data: s.saveData } : s);
    const remapScore = (r) => (r ? { ...r, user: r.name } : r);

    const saveGame = {
        all: async () => {
            const o = await api('/save');
            if (!o) return o;
            const out = {};
            for (const k of Object.keys(o)) out[k] = remapSlot(o[k]);
            return out;
        },
        load: async (slot) => remapSlot(await api(`/save/${slot}`)),
        write: async (saveData, overwrite, slot, name) => {
            const r = await api(`/save/${slot}`, {
                method: 'POST',
                body: JSON.stringify({ saveData, overwrite: overwrite !== false, name: name || '' }),
            });
            return !!r?.success;
        },
        wipe: async () => {
            const r = await api('/save', { method: 'DELETE' });
            return !!r?.success;
        },
    };

    const leaderboard = {
        get: async (slug, limit = 50, page = 1) => {
            const rows = await api(`/leaderboard/${slug}?limit=${limit}&page=${page}`);
            return Array.isArray(rows) ? rows.map(remapScore) : rows;
        },
        getMy: async (slug, _limit = 1) => {
            const r = await api(`/leaderboard/${slug}/me`);
            return r ? [remapScore(r)] : [];
        },
        postScore: (slug, score) => api(`/leaderboard/${slug}`, {
            method: 'POST',
            body: JSON.stringify({ score }),
        }),
    };

    const inventory = {
        get: () => api('/inventory'),
        purchase: (slug) => api(`/inventory/${slug}`, { method: 'POST' }),
    };

    return { user, saveGame, leaderboard, inventory, ensureAuth };
}
